import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NeoButtonV2Module } from '@neogov/pattern-library/neo-button-v2';
import { IconComponent } from '../icon';

@Component({
  selector: 'cm-link',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink, NeoButtonV2Module, IconComponent],
  templateUrl: './link.component.html',
  styleUrl: './link.component.less',
})
export class LinkComponent {
  // Navigation Inputs
  public readonly routerLinkName = input<string | undefined>(undefined);
  public readonly href = input<string | undefined>(undefined);
  public readonly external = input<boolean | undefined>(undefined);

  // Behavior Inputs
  public readonly target = input<'_blank' | '_self' | undefined>(undefined);
  public readonly rel = input<string>('noopener noreferrer');
  public readonly disabled = input<boolean>(false);

  // Styles Inputs
  public readonly showIcon = input<boolean>(true);
  public readonly icon = input<string | undefined>(undefined);
  public readonly color = input<'dark' | 'primary'>('primary');
  /** When true, the underline is hidden by default and only shown on hover/focus. */
  public readonly underlineOnHover = input<boolean>(false);

  // Accessibility & Testing
  public readonly ariaLabel = input<string | undefined>(undefined);
  public readonly autId = input<string | undefined>(undefined);

  /**
   * Emitted when an action-mode link (no href/routerLink) is clicked. Lets the component be used
   * as a link-styled button for in-page actions (e.g. "Edit") that don't navigate.
   */
  public readonly clicked = output<void>();

  /**
   * Action mode: the link has no navigation target, so it renders a <button> that emits `clicked`
   * instead of an anchor. This is a link-styled action, not navigation.
   */
  public readonly isAction = computed(() => !this.routerLinkName() && !this.href());

  public onActionClick(): void {
    if (this.disabled()) {
      return;
    }
    this.clicked.emit();
  }

  /**
   * Determines if the link is external based on:
   * 1. routerLinkName presence (always internal)
   * 2. Explicit external input
   * 3. URL pattern detection (http:// or https://)
   */
  public readonly isExternalLink = computed(() => {
    if (this.routerLinkName()) {
      return false;
    }

    if (this.external() !== undefined) {
      return this.external()!;
    }

    const url = this.href();
    return url ? url.startsWith('http://') || url.startsWith('https://') : false;
  });

  /**
   * Determines which template to render:
   * - true: Use routerLink anchor
   * - false: Use href anchor
   */
  public readonly isInternalNavigation = computed(() => {
    return !!this.routerLinkName() || !this.isExternalLink();
  });

  /**
   * Resolves the target attribute:
   * - Explicit target input takes precedence
   * - External links default to _blank
   * - Internal links default to _self
   */
  public readonly linkTarget = computed(() => {
    if (this.target()) {
      return this.target();
    }
    return this.isExternalLink() ? '_blank' : '_self';
  });

  /**
   * Resolves the rel attribute (for external links or when target is _blank)
   */
  public readonly linkRel = computed(() => {
    return this.isExternalLink() || this.linkTarget() === '_blank' ? this.rel() : undefined;
  });

  /**
   * Determines if icon should be displayed:
   * - showIcon must be true
   * - Custom icon provided OR link is external OR target is _blank
   */
  public readonly shouldShowIcon = computed(() => {
    return (
      this.showIcon() && (!!this.icon() || this.isExternalLink() || this.target() === '_blank')
    );
  });
}
